import { Component, HostListener, OnInit } from '@angular/core';
import { Route, Router, RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ConnectionService } from '../../service/connection.service';
import { TourService } from '../../service/tour.service';
import { EventService } from '../../service/event.service';
import { EventsMapComponent } from '../matrimoni/events-map.component';
import { SubscriptionService } from '../../service/subscription.service';
import { InfoTooltipComponent } from '../../components/info-tooltip/info-tooltip.component';
import { resolveDepositEuro } from '../../utility/deposit';
import { IconComponent } from '../../components/icon/icon.component';
import { LoadErrorComponent } from '../../components/load-error/load-error.component';
import { isQuote } from '../../utility/quote';
import { compareEventDate } from '../../utility/event-sort';

const DEADLINE_DAYS = 7;

interface Deadline {
  id: string;
  name: string;
  date: Date;
  kind: 'quote' | 'signature';
}

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css'],
  imports: [CommonModule, RouterLink, TranslatePipe, EventsMapComponent, InfoTooltipComponent, IconComponent, LoadErrorComponent],
  host: { '[class.dashboard-mode]': 'hasEvents' },
})
export class HomeComponent implements OnInit {
  nextWedding: any = null;
  profileCompleted = false;

  // ── DASHBOARD ──
  userName = '';
  isBeauty = true; // makeup/hair → "Prova", altrimenti "Sopralluogo"
  hasEvents = false;
  countUpcoming = 0;
  countToGenerate = 0;
  openBalance = 0;
  countQuotesSent = 0;

  reminders: { text: string; done: boolean }[] = [];
  deadlines: Deadline[] = [];
  readonly deadlineDays = DEADLINE_DAYS;

  agenda: any[] = []; // prossimi eventi (max 5) con badge stato
  todos: { icon: string; textKey: string; count: number; amount: number; link: string }[] = [];
  nextTrial: any = null; // prossima prova/sopralluogo

  currentYear = new Date().getFullYear();

  // Fino a oggi (da sempre)
  statEventsDone = 0;
  statSigned = 0;
  statCollected = 0;

  // Trasferte
  statKmYear = 0;
  statKmUpcoming = 0;
  statTravelCostYear = 0;
  nextTravelKm = 0;
  nextDays: number | null = null;

  // Mappa del mese
  monthEvents: any[] = [];

  // La mappa (dash-map) esiste solo in .desktop-home, nascosta via CSS su mobile:
  // senza questo flag Angular la istanzierebbe comunque, caricando Google Maps a vuoto.
  isDesktopViewport = typeof window !== 'undefined' && window.innerWidth > 768;

  @HostListener('window:resize')
  onResize(): void {
    this.isDesktopViewport = window.innerWidth > 768;
  }

  constructor(
    private connection: ConnectionService,
    private router: Router,
    private translate: TranslateService,
    private tour: TourService,
    private eventService: EventService,
    private subscription: SubscriptionService,
  ) {}

  get canProStats(): boolean {
    return this.subscription.canPro();
  }

  goToPlans() {
    this.router.navigate(['/abbonamenti']);
  }

  private get locale(): string {
    const lang = this.translate.currentLang || this.translate.defaultLang || 'it';
    const map: Record<string, string> = { it: 'it-IT', en: 'en-GB', es: 'es-ES' };
    return map[lang] ?? 'it-IT';
  }

  ngOnInit() {
    this.profileCompleted = localStorage.getItem('profile_completed') === 'true';
    this.tour.home();
    this.subscription.loadEntitlements();
    this.loadUserName();
    this.loadEvents();
    this.loadReminders();
  }

  // Eventi non arrivati: errore con Riprova, non la schermata di benvenuto da account vuoto
  eventsError = false;

  loadEvents() {
    this.connection.get(
      'events',
      (all: any[]) => {
        this.eventsError = false;
        this.hasEvents = (all?.length ?? 0) > 0;

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        // I preventivi non ancora confermati restano fuori da agenda e numeri
        const data = all.filter((m) => !isQuote(m));
        const pendingQuotes = all.filter((m) => isQuote(m) && new Date(m.event.date) >= today);
        const upcoming = data
          .filter((m) => new Date(m.event.date) >= today)
          .sort(compareEventDate);

        this.nextWedding = upcoming[0] ?? null;
        this.countUpcoming = upcoming.length;
        this.countToGenerate = upcoming.filter(
          (m) => !m.event?.contract?.file,
        ).length;
        this.openBalance = upcoming.reduce(
          (sum, m) => sum + this.remainingBalance(m),
          0,
        );

        // Agenda: prossimi 5 con badge stato precalcolato
        upcoming.forEach((m) => (m._status = this.computeStatus(m)));
        this.agenda = upcoming.slice(0, 5);

        // Prossima prova/sopralluogo
        this.nextTrial =
          data
            .filter((m) => m.event?.test_date && new Date(m.event.test_date) >= today)
            .sort(
              (a, b) =>
                new Date(a.event.test_date).getTime() -
                new Date(b.event.test_date).getTime(),
            )[0] ?? null;

        this.nextTravelKm = +this.nextWedding?.financial?.travel_fee?.distance_km || 0;
        this.nextDays = this.nextWedding
          ? Math.round((new Date(this.nextWedding.event.date).setHours(0, 0, 0, 0) - today.getTime()) / 86400000)
          : null;

        this.countQuotesSent = all.filter((m) => isQuote(m) && m.quote_request?.sent_at).length;

        this.buildTodos(upcoming, pendingQuotes);
        this.buildDeadlines(all);
        this.buildPastSummary(data, today);
        this.buildTravelStats(data, upcoming);
        this.buildMonthEvents(data);
      },
      () => (this.eventsError = true),
    );
  }

  // '@' → niente loader; 'auth/profile' è cacheato (già caricato dalla navbar)
  private loadUserName() {
    this.connection.get(
      '@auth/profile',
      (data: any) => {
        this.userName = data?.user?.name ?? '';
        const cats: string[] = data?.user?.professional_categories ?? [];
        this.isBeauty = !cats.length || cats.some((c) => c === 'makeup' || c === 'hair');
      },
      () => {},
    );
  }

  // Stessa lista del calendario; '@' → niente loader
  private loadReminders() {
    this.connection.get(
      '@auth/reminders',
      (data: { text: string; done: boolean }[]) => {
        this.reminders = (data || []).filter((r) => !r.done).slice(0, 5);
      },
      () => {},
    );
  }

  private remainingBalance(m: any): number {
    return this.eventService.calcolaSaldo(m);
  }

  private buildTravelStats(data: any[], upcoming: any[]) {
    const km = (m: any) => +m?.financial?.travel_fee?.distance_km || 0;
    const cost = (m: any) => +m?.financial?.travel_fee?.cost || 0;
    const yearEvents = data.filter(
      (m) => new Date(m.event.date).getFullYear() === this.currentYear,
    );
    this.statKmYear = Math.round(yearEvents.reduce((s, m) => s + km(m), 0));
    this.statKmUpcoming = Math.round(upcoming.reduce((s, m) => s + km(m), 0));
    this.statTravelCostYear = yearEvents.reduce((s, m) => s + cost(m), 0);
  }

  private buildMonthEvents(data: any[]) {
    const now = new Date();
    this.monthEvents = data.filter((m) => {
      const d = new Date(m.event.date);
      return (
        d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()
      );
    });
  }

  /** Acconto dell'evento risolto in euro (gestisce sia importo fisso che percentuale sul totale). */
  private depositEuro(m: any): number {
    return resolveDepositEuro(m.financial?.deposit, this.eventService.computeTotal(m));
  }

  // Stessa regola della scheda evento: firmato dal cliente o PDF firmato presente
  private isSigned(m: any): boolean {
    const sr = m.signature_request;
    return !!(m.event?.contract?.is_signed || sr?.signed_contract_file || sr?.signed_at);
  }

  // Stato "a colpo d'occhio" di un evento (priorità: contratto → firma → acconto)
  private computeStatus(m: any): { icon: string; key: string; cls: string } {
    if (!m.event?.contract?.file)
      return { icon: 'alert', key: 'home.dashboard.st_to_generate', cls: 'warn' };
    if (!this.isSigned(m))
      return { icon: 'sign', key: 'home.dashboard.st_to_sign', cls: 'info' };
    if (this.depositEuro(m) > 0 && !m.financial?.deposit?.paid)
      return { icon: 'euro', key: 'home.dashboard.st_deposit', cls: 'money' };
    return { icon: 'check', key: 'home.dashboard.st_ok', cls: 'ok' };
  }

  private buildTodos(upcoming: any[], pendingQuotes: any[]) {
    const toGen = upcoming.filter((m) => !m.event?.contract?.file);
    const toSign = upcoming.filter(
      (m) => m.event?.contract?.file && !this.isSigned(m),
    );
    const depos = upcoming.filter(
      (m) => this.depositEuro(m) > 0 && !m.financial?.deposit?.paid,
    );
    const deposSum = depos.reduce(
      (s, m) => s + this.depositEuro(m),
      0,
    );

    this.todos = [];
    if (pendingQuotes.length)
      this.todos.push({
        icon: 'note',
        textKey: 'quote.home_todo',
        count: pendingQuotes.length,
        amount: pendingQuotes.reduce((s, m) => s + this.eventService.computeTotal(m), 0),
        link: '/eventi?stato=preventivo',
      });
    if (toGen.length)
      this.todos.push({ icon: 'alert', textKey: 'home.dashboard.to_generate', count: toGen.length, amount: 0, link: '/eventi' });
    if (toSign.length)
      this.todos.push({ icon: 'sign', textKey: 'home.dashboard.to_sign', count: toSign.length, amount: 0, link: '/contratti' });
    if (depos.length)
      this.todos.push({ icon: 'euro', textKey: 'home.dashboard.deposits_to_collect', count: depos.length, amount: deposSum, link: '/eventi' });
  }

  // Preventivi e firme il cui link scade entro DEADLINE_DAYS giorni
  private buildDeadlines(all: any[]) {
    const now = Date.now();
    const limit = now + DEADLINE_DAYS * 86400000;
    const inWindow = (d: any) => {
      const t = d ? new Date(d).getTime() : NaN;
      return t >= now && t <= limit;
    };
    const name = (m: any) => `${m.client?.name ?? ''} ${m.client?.lastname ?? ''}`.trim();

    const quotes: Deadline[] = all
      .filter((m) => isQuote(m) && inWindow(m.quote_request?.expires_at))
      .map((m) => ({ id: m._id, name: name(m), date: new Date(m.quote_request.expires_at), kind: 'quote' }));
    const signatures: Deadline[] = all
      .filter((m) => !isQuote(m) && !this.isSigned(m) && inWindow(m.signature_request?.expires_at))
      .map((m) => ({ id: m._id, name: name(m), date: new Date(m.signature_request.expires_at), kind: 'signature' }));

    this.deadlines = [...quotes, ...signatures].sort((a, b) => a.date.getTime() - b.date.getTime());
  }

  private buildPastSummary(data: any[], today: Date) {
    this.statEventsDone = data.filter((m) => m.event?.date && new Date(m.event.date) < today).length;
    this.statSigned = data.filter((m) => this.isSigned(m)).length;
    this.statCollected = data.reduce((s, m) => s + this.collected(m), 0);
  }

  // Acconto e saldo segnati come pagati, trasferta esclusa
  private collected(m: any): number {
    const total = this.eventService.computeTotal(m);
    const deposit = this.depositEuro(m);
    const travel = +m.financial?.travel_fee?.cost || 0;
    let sum = m.financial?.deposit?.paid ? deposit : 0;
    if (m.financial?.balance?.paid) sum += Math.max(0, total - deposit - travel);
    return sum;
  }

  get countdownKey(): string {
    if (this.nextDays === 0) return 'home.dashboard.countdown_today';
    if (this.nextDays === 1) return 'home.dashboard.countdown_tomorrow';
    return 'home.dashboard.countdown_days';
  }

  dayOf(date: string): string {
    return String(new Date(date).getDate());
  }

  monthOf(date: string): string {
    return new Date(date).toLocaleDateString(this.locale, { month: 'short' }).replace('.', '');
  }

  openTrial() {
    if (this.nextTrial) this.openEvent(this.nextTrial._id);
  }

  deadlineKey(d: Deadline): string {
    return d.kind === 'quote' ? 'home.dashboard.quote_expires' : 'home.dashboard.signature_expires';
  }

  get trialTitleKey(): string {
    return this.isBeauty ? 'home.dashboard.next_trial' : 'home.dashboard.next_inspection';
  }

  formatDateShort(date: string | Date): string {
    return new Date(date).toLocaleDateString(this.locale, {
      day: 'numeric',
      month: 'short',
    });
  }

  openEvent(id: string) {
    this.router.navigate(['evento/' + id]);
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat(this.locale, {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(value);
  }

  getServices(m: any): string[] {
    const tags: string[] = [];
    if (
      m.event?.service?.some(
        (s: any) => s.checked && s.service?.toLowerCase().includes('trucco'),
      )
    )
      tags.push('Trucco');
    if (
      m.event?.service?.some(
        (s: any) => s.checked && s.service?.toLowerCase().includes('capelli'),
      )
    )
      tags.push('Capelli');
    if (tags.length === 0) {
      m.event?.service
        ?.filter((s: any) => s.checked)
        .slice(0, 2)
        .forEach((s: any) => tags.push(s.service));
    }
    return tags;
  }

  formatDate(date: string | Date): string {
    return new Date(date).toLocaleDateString(this.locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }

  formatTime(time: string): string {
    return time ?? '';
  }

  goto(path: string) {
    if (path === 'wedding')
      this.router.navigate(['evento/' + this.nextWedding._id]);
    else this.router.navigateByUrl(path);
  }

  onCtaClick() {
    if (this.profileCompleted) this.router.navigate(['/eventi/nuovo']);
    else this.router.navigate(['/profilo']);
  }
}
